import { FileDown } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { Markdown } from './Markdown';

/** How long to wait for diagrams before printing anyway (they then print as their source). */
const DIAGRAM_WAIT_MS = 5000;

/**
 * Saves a Markdown document as an A4 PDF through the browser's print dialog (JIRA-1). While printing, only a
 * print layout (title, details, body) is shown; `@page` in app.css fixes the paper to A4. The document title
 * becomes the suggested file name.
 */
export function DownloadPdfButton({
  fileName,
  title,
  details,
  body,
}: {
  fileName: string;
  title: string;
  details: ReactNode;
  body: string;
}) {
  const [printing, setPrinting] = useState(false);
  const sheet = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!printing) return;
    let cancelled = false;
    const pageTitle = document.title;
    const finish = () => {
      document.body.classList.remove('printing');
      document.title = pageTitle;
      setPrinting(false);
    };
    window.addEventListener('afterprint', finish, { once: true });
    document.body.classList.add('printing');
    document.title = fileName;
    const started = Date.now();
    const printWhenReady = () => {
      if (cancelled) return;
      const pending = sheet.current?.querySelector('.mermaid-pending');
      if (pending && Date.now() - started < DIAGRAM_WAIT_MS) {
        setTimeout(printWhenReady, 50);
        return;
      }
      window.print();
    };
    requestAnimationFrame(printWhenReady);
    return () => {
      cancelled = true;
      window.removeEventListener('afterprint', finish);
      document.body.classList.remove('printing');
      document.title = pageTitle;
    };
  }, [printing, fileName]);

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        icon={<FileDown size={14} />}
        loading={printing}
        onClick={() => setPrinting(true)}
      >
        Download PDF
      </Button>
      {printing &&
        createPortal(
          <div className="print-sheet" ref={sheet}>
            <header className="print-header">
              <h1>{title}</h1>
              <div className="small">{details}</div>
            </header>
            <Markdown>{body}</Markdown>
          </div>,
          document.body,
        )}
    </>
  );
}
